/**
 * SSO callback landing page.
 *
 * The oxbo-auth OAuth callback has already set the HttpOnly session cookies and
 * 302-redirected the browser here (the per-org post_login_redirect_uri →
 * https://admin.<env>.oxbo.energy/auth/callback). There is no token in the URL
 * to read; we simply resolve identity via cookie-auth getMe() and route on.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

export function AuthCallbackPage(): ReactNode {
  const { completeSsoCallback } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      // Surface a provider-side error if present (?error=...).
      const params = new URLSearchParams(window.location.search);
      const oauthError = params.get("error");
      if (oauthError) {
        if (active) setError(`Sign-in was cancelled or failed (${oauthError}).`);
        return;
      }
      const id = await completeSsoCallback();
      if (!active) return;
      if (id) {
        navigate("/", { replace: true });
      } else {
        setError("Could not establish a session. Please try signing in again.");
      }
    })();
    return () => {
      active = false;
    };
  }, [completeSsoCallback, navigate]);

  return (
    <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-600">
      {error ? (
        <div className="max-w-sm text-center">
          <p className="mb-3 text-red-600">{error}</p>
          <a href="/login" className="text-sm text-oxbo underline">
            Back to sign in
          </a>
        </div>
      ) : (
        <span role="status" className="animate-pulse">
          Completing sign-in…
        </span>
      )}
    </div>
  );
}
